import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ArrowRight, Newspaper } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const News = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const url = filter === 'all' ? `${API}/news?published=true` : `${API}/news?published=true&category=${filter}`;
        const response = await axios.get(url);
        setNews(response.data);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [filter]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen" data-testid="news-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-indigo-600 to-purple-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Newspaper className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('newsPage.title')}</h1>
          <p className="text-xl text-indigo-100">{t('newsPage.subtitle')}</p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {['all', 'news', 'events', 'updates'].map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                data-testid={`filter-${category}`}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  filter === category
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t(`newsPage.filter_${category}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News List */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12" data-testid="news-loading">
              <p className="text-gray-500">{t('common.loading')}</p>
            </div>
          ) : news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="news-grid">
              {news.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/news/${item.id}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 duration-200" 
                  data-testid={`news-card-${item.id}`}
                >
                  {item.image_url && (
                    <img src={item.image_url} alt={language === 'de' ? item.title_de : item.title_en} className="w-full h-48 object-cover" />
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-indigo-600 uppercase">{item.category}</span>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(item.created_at)}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">
                      {language === 'de' ? item.title_de : item.title_en}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {language === 'de' ? (item.excerpt_de || item.content_de) : (item.excerpt_en || item.content_en)}
                    </p>
                    <div className="flex items-center text-indigo-600 font-medium">
                      {language === 'de' ? 'Mehr lesen' : 'Read more'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">{language === 'de' ? 'Keine News verfügbar' : 'No news available'}</p>
            </div>
          )}
        </div>
      </section>

      {/* Facebook Feed Section */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Folge uns auf Facebook' : 'Follow us on Facebook'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'de' 
                ? 'Bleib immer auf dem Laufenden mit unseren neuesten Updates und Bildern!' 
                : 'Stay up to date with our latest updates and pictures!'}
            </p>
          </div>
          
          <div className="flex justify-center">
            <div className="bg-white rounded-lg shadow-xl p-4">
              <iframe
                src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2FSurfmotion1&tabs=timeline&width=500&height=700&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                width="500"
                height="700"
                style={{ border: 'none', overflow: 'hidden' }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                title="Surfmotion Facebook Page"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;